// The ONE request field for a lot money amount (startPrice, reserve, increment,
// hammerPrice, loadOutFee): a MoneyWire validated and decoded by Zod, so the item
// form's zodResolver and every lot route's parseBody read the same rules
// (ADR 016 — Form Validation, ADR 060 — Money Type).
//
// BL-2117 built the field for the increment; BL-2131 moved Starting Price,
// Reserve and Load-out Fee onto it on the lot PATCH, both create doors and the
// item form, and deleted the number validator (`validateLotMonetaryFields`) it
// replaced (ADR 044 — Must Consolidate).

import Decimal from 'decimal.js';
import {
  CURRENCY_LABELS,
  exceedsMoneyCeiling,
  formatMoneyText,
  moneyCeiling,
  fromWire,
  isSupportedCurrency,
  minorUnits,
  type Money,
  type MoneyWire,
} from '@auctogon/core/money';
import { decodeWireAmount } from '@auctogon/core/realtime/payloads/money-wire-amount';
import { MONEY_WIRE_MALFORMED_MESSAGE } from '@auctogon/core/schemas/money-wire';
import { z } from '@auctogon/core/zod';

import { lotAmountRefusal } from './lot-amount';

/** A lot money field the shared field governs; picks its label and its unit rule. */
export type LotMoneyField = 'startPrice' | 'reserve' | 'increment' | 'hammerPrice' | 'loadOutFee';

/** The visible form label of each lot money field — what an operator reads. */
const LOT_MONEY_FIELD_LABELS: Record<LotMoneyField, string> = {
  startPrice: 'Starting price',
  reserve: 'Reserve price',
  increment: 'Bid increment',
  hammerPrice: 'Hammer price',
  loadOutFee: 'Load-out fee',
};

/** Which rule a lot amount broke: not a non-negative number, or above the column. */
export type LotMoneyRule = 'invalid' | 'tooLarge';

/**
 * An amount as it reads inside an operator message, in `currency`.
 *
 * Truncated (never rounded) to the currency's minor units first: the bound is
 * the column's `9,999,999,999.99`, and `format()` would round it UP to
 * `¥10,000,000,000` for yen — a value the rule itself refuses. With no supported
 * currency the amount prints bare, with no sign: never a guessed "$" (ADR 060 —
 * Money Type), and never core's em-dash, which would hide the number.
 */
function amountInMessage(amount: string, currency: string | null): string {
  if (!isSupportedCurrency(currency)) {
    return Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  const truncated = new Decimal(amount).toDecimalPlaces(minorUnits(currency), Decimal.ROUND_DOWN);
  return formatMoneyText(truncated.toFixed(), currency, { noCents: 'when-whole' });
}

/**
 * The house money ceiling (BUG-1903's `moneyCeiling`) as it reads inside an
 * operator message: ¥9,999,999,999, $9,999,999,999.99. With no supported
 * currency the digits print bare, with no sign — the ceiling's major-unit value
 * at two decimals, the widest any supported currency allows.
 */
function ceilingInMessage(currency: string | null): string {
  if (isSupportedCurrency(currency)) {
    return formatMoneyText(moneyCeiling(currency), currency, { noCents: 'when-whole' });
  }
  // Digits only — the reference currency's sign is never printed (ADR 060).
  return amountInMessage(moneyCeiling(TWO_DECIMAL_REFERENCE).toDecimal().toFixed(), null);
}

/** A two-minor-unit currency whose ceiling supplies the bare digits above. */
const TWO_DECIMAL_REFERENCE = 'USD';

/**
 * The currency's unit word ("dollar", "yen", "euro"), or null when unknown.
 * Read off core's own `CURRENCY_LABELS`, whose last word names the unit
 * ("Canadian Dollar", "Japanese Yen"), so there is no second per-currency table.
 */
export function currencyUnitWord(currency: string | null): string | null {
  if (!isSupportedCurrency(currency)) return null;
  const words = CURRENCY_LABELS[currency].split(' ');
  return words[words.length - 1]!.toLowerCase();
}

/**
 * The operator-facing message for a lot money rule, in the record's currency
 * (BUG-1284 made these name the field's VISIBLE label; BUG-1894 names the
 * amount in the lot's currency, since the field above it carries that sign).
 *
 * This is what lands in a route's `fieldErrors`, inline under the field. A USD
 * lot reads exactly as it did before BUG-1894.
 *
 * @param opts.field - the money field that failed
 * @param opts.rule - `invalid` (negative / not a number / fractional increment) or `tooLarge`
 * @param opts.currency - the record's ISO 4217 code; null or unsupported prints the amount bare
 * @returns e.g. "Starting price must be ¥9,999,999,999 or less"
 */
export function lotMoneyDisplayError(opts: {
  field: LotMoneyField;
  rule: LotMoneyRule;
  currency: string | null;
}): string {
  const label = LOT_MONEY_FIELD_LABELS[opts.field];
  if (opts.rule === 'tooLarge') return `${label} must be ${ceilingInMessage(opts.currency)} or less`;
  const zero = amountInMessage('0', opts.currency);
  if (opts.field !== 'increment') return `${label} must be ${zero} or more`;
  // The increment carries a whole-units rule too, which the message names.
  const unit = currencyUnitWord(opts.currency);
  return `${label} must be a whole ${unit == null ? '' : `${unit} `}amount of ${zero} or more`;
}

/**
 * The issue (if any) with one decoded lot amount, in the order an operator would
 * fix it: the sign, the column's ceiling, then the field's unit — whole units for
 * the increment (the ladder steps in whole amounts), the currency's minor unit
 * for every other price (¥1, $0.01; BL-2020's `lotAmountRefusal` owns that copy).
 */
function lotMoneyIssue(field: LotMoneyField, money: Money): string | null {
  const value = money.toDecimal();
  const currency = money.currency;
  if (value.isNegative()) return lotMoneyDisplayError({ field, rule: 'invalid', currency });
  // BUG-1903: the house money ceiling, in the amount's own currency — the
  // `numeric(12,2)` lot column bound (BL-1978), truncated to the minor unit.
  if (exceedsMoneyCeiling(money)) return lotMoneyDisplayError({ field, rule: 'tooLarge', currency });
  if (field === 'increment') {
    return value.isInteger() ? null : lotMoneyDisplayError({ field, rule: 'invalid', currency });
  }
  // The lot's own currency is checked at the route, which holds the lot row; here
  // the amount is judged against the currency it names, so only the step can fail.
  return lotAmountRefusal({ amount: money, lotCurrency: currency })?.message ?? null;
}

/**
 * BL-2117 — the ONE Zod field for a lot money amount on the wire: a `MoneyWire`
 * (`{ amount: '<decimal string>', currency }`, ADR 060 — Money Type) whose amount
 * is a storable lot price. Validates only — the form's `zodResolver` holds the
 * wire as typed; {@link lotMoneyWireField} is the same rules plus the one decode
 * a route needs. Every issue lands on the field itself, so `parseBody` reports it
 * as `fieldErrors.<field>` and the form shows it under the input.
 *
 * A bare number, a non-decimal string, or an unsupported currency is refused as
 * malformed ("Enter an amount like 1234.50") — there is no number decode.
 *
 * @param field - Which lot money field this is; picks its copy and its unit rule.
 * @returns A schema that parses a `MoneyWire` unchanged.
 * @sideEffect none — pure.
 */
export function lotMoneyWireSchema(field: LotMoneyField) {
  return z
    .custom<MoneyWire>((value) => decodeWireAmount(value).kind === 'money', MONEY_WIRE_MALFORMED_MESSAGE)
    .superRefine((wire, ctx) => {
      const decoded = decodeWireAmount(wire);
      if (decoded.kind !== 'money') return;
      const message = lotMoneyIssue(field, decoded.money);
      if (message) ctx.addIssue({ code: 'custom', path: [], message });
    });
}

/**
 * {@link lotMoneyWireSchema} plus the decode: a route's `parseBody` /
 * `validateParsed` hands its write a `Money`, decoded once (BL-2117). Whether
 * the amount is in the LOT's currency is the route's check (`lotAmountRefusal`),
 * because only the route holds the lot row.
 *
 * @param field - Which lot money field this is.
 * @returns A schema that parses a `MoneyWire` into a `Money`.
 * @sideEffect none — pure.
 */
export function lotMoneyWireField(field: LotMoneyField) {
  return lotMoneyWireSchema(field).transform((wire) => fromWire(wire));
}
